import { apiClient } from './apiClient';

export type ChatSessionStatus =
  | 'ai_active'
  | 'human_requested'
  | 'human_active'
  | 'closed';

export interface InboxMessage {
  id: string;
  role: string;
  sender_type: string;
  content: string;
  created_at: string;
  metadata?: Record<string, unknown>;
}

export interface InboxAssignee {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

export interface InboxSessionRow {
  id: string;
  status: ChatSessionStatus;
  page_url: string;
  last_message_at: string | null;
  created_at: string;
  updated_at: string;
  takeover_requested_at: string | null;
  message_count: number;
  last_preview?: string;
  visitor_name?: string;
  visitor_email?: string;
  assignee_email?: string;
  assigned_user?: InboxAssignee | null;
}

export interface InboxSessionDetail extends InboxSessionRow {
  messages: InboxMessage[];
  visitor_phone?: string;
  visitor_company?: string;
  crm_lead_id?: string | null;
  closed_at?: string | null;
  takeover_accepted_at?: string | null;
  removed_from_inbox?: boolean;
}

export interface InboxPollResult {
  human_requested_count: number;
  new_activity_count: number;
  server_time: string;
}

export interface InboxListDebug {
  legacy_engaged_count: number;
  all_sessions_count: number;
  open_engaged_count: number;
  removed_engaged_count: number;
  visible_engaged_count: number;
}

export interface InboxListResponse {
  count: number;
  results: InboxSessionRow[];
  human_requested_count: number;
  tenant_id?: string;
  tenant_name?: string;
  debug?: InboxListDebug;
  page?: number;
  page_size?: number;
}

export const chatbotInboxService = {
  listInbox(params: {
    status?: string;
    q?: string;
    page?: number;
    page_size?: number;
  }): Promise<InboxListResponse> {
    return apiClient.get('/chatbot/manage/inbox/', params);
  },

  getSession(sessionId: string): Promise<InboxSessionDetail> {
    return apiClient.get(`/chatbot/manage/inbox/${sessionId}/`);
  },

  pollInbox(since?: string): Promise<InboxPollResult> {
    return apiClient.get('/chatbot/manage/inbox/poll/', since ? { since } : {});
  },

  takeover(sessionId: string): Promise<InboxSessionDetail> {
    return apiClient.post(`/chatbot/manage/inbox/${sessionId}/takeover/`, {});
  },

  release(sessionId: string): Promise<InboxSessionDetail> {
    return apiClient.post(`/chatbot/manage/inbox/${sessionId}/release/`, {});
  },

  close(sessionId: string): Promise<InboxSessionDetail> {
    return apiClient.post(`/chatbot/manage/inbox/${sessionId}/close/`, {});
  },

  reply(sessionId: string, content: string): Promise<InboxSessionDetail> {
    return apiClient.post(`/chatbot/manage/inbox/${sessionId}/reply/`, { content });
  },

  remove(sessionId: string): Promise<void> {
    return apiClient.delete(`/chatbot/manage/inbox/${sessionId}/remove/`);
  },

  restore(sessionId: string): Promise<InboxSessionDetail> {
    return apiClient.post(`/chatbot/manage/inbox/${sessionId}/restore/`, {});
  },

  listNotifications(): Promise<{ count: number; results: { id: string; title: string; session_id: string }[] }> {
    return apiClient.get('/chatbot/manage/notifications/');
  },

  markNotificationRead(id: string): Promise<void> {
    return apiClient.patch(`/chatbot/manage/notifications/${id}/read/`, {});
  },
};
